import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ActionName, CardDisplay, ElementName, SmartLinkPosition, SmartLinkSize, SmartLinkDirection } from '../../../../../constants';
import { FooterBlock, MetadataBlock, RelatedUrlsBlock, SnippetBlock, TitleBlock, AISummaryBlock, CustomBlock } from '../../../../FlexibleCard/components/blocks';
import { footerBlockCss, hiddenSnippetStyles, metadataBlockCss } from './styled';
import FlexibleCard from '../../../../FlexibleCard';
import { getMetadata } from '../../../utils';
import ImagePreview from '../../ImagePreview';
import { messages } from '../../../../../messages';
import { FormattedMessage } from 'react-intl-next';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { getCanBeDatasource } from '../../../../../state/helpers';
import useAISummaryAction from '../../../../../state/hooks/use-ai-summary-action';
import { SmartLinkStatus } from '../../../../../constants';
import { di } from 'react-magnetic-di';
export const toFooterActions = (cardActions, onActionClick) => {
  const followAction = {
    hideIcon: true,
    name: ActionName.FollowAction
  };
  const actions = cardActions.map(action => {
    if (action.id === 'preview-content') {
      return {
        content: /*#__PURE__*/React.createElement(FormattedMessage, messages.preview_improved),
        name: ActionName.CustomAction,
        onClick: () => {
          if (onActionClick) {
            onActionClick(action.id);
          }
          return action.invoke();
        },
        testId: action.id,
        appearance: 'primary'
      };
    }
    return {
      content: action.text,
      name: ActionName.CustomAction,
      onClick: () => {
        if (onActionClick) {
          onActionClick(action.id);
        }
        return action.invoke();
      },
      testId: action.id
    };
  });
  return [followAction, ...actions];
};

//This component encapsulates useAISummarySmartLink hook under the AI Summary FF 'platform.linking-platform.smart-card.hover-card-ai-summaries'
const ConnectedAIBlock = ({
  bottomPrimary,
  imagePreview,
  url
}) => {
  const {
    state: {
      status,
      content
    }
  } = useAISummaryAction(url);
  const showData = status === 'ready' || status === 'error' ||
  // Description & bottom metadata should only disappear when we start getting summary content
  !content && status === 'loading';
  return showData ? /*#__PURE__*/React.createElement(React.Fragment, null, !imagePreview && /*#__PURE__*/React.createElement(SnippetBlock, {
    testId: 'connected-AI',
    status: SmartLinkStatus.Resolved
  }), /*#__PURE__*/React.createElement(MetadataBlock, {
    primary: bottomPrimary,
    size: SmartLinkSize.Large,
    overrideCss: metadataBlockCss,
    maxLines: 1,
    status: SmartLinkStatus.Resolved
  })) : null;
};
const HoverCardResolvedView = ({
  flexibleCardProps,
  titleBlockProps,
  analytics,
  cardState,
  cardActions = [],
  isAISummaryEnabled,
  onActionClick,
  extensionKey,
  url
}) => {
  var _cardState$details;
  const canBeDatasource = getCanBeDatasource(cardState.details);
  useEffect(() => {
    // Since this hover view is only rendered on resolved status,
    // there is no need to check for statuses.
    analytics.ui.renderSuccessEvent({
      display: CardDisplay.HoverCardPreview,
      status: cardState.status,
      canBeDatasource
    });
  }, [analytics.ui, cardState.status, canBeDatasource]);
  const footerActions = useMemo(() => toFooterActions(cardActions, onActionClick), [cardActions, onActionClick]);
  const data = (_cardState$details = cardState.details) === null || _cardState$details === void 0 ? void 0 : _cardState$details.data;
  const {
    primary,
    secondary,
    tertiary
  } = useMemo(() => {
    return getMetadata(extensionKey, data);
  }, [data, extensionKey]);
  const snippetHeight = React.useRef(0);
  const snippetBlockRef = useRef(null);
  const onSnippetRender = useCallback(() => {
    var _snippetBlockRef$curr, _snippetBlockRef$curr2;
    snippetHeight.current = (_snippetBlockRef$curr = (_snippetBlockRef$curr2 = snippetBlockRef.current) === null || _snippetBlockRef$curr2 === void 0 ? void 0 : _snippetBlockRef$curr2.getBoundingClientRect().height) !== null && _snippetBlockRef$curr !== void 0 ? _snippetBlockRef$curr : 0;
  }, []);

  // We want to maintain the height of the HoverCard while summarizing and pass this height to the AISummaryBlock.
  // This approach will prevent any abrupt changes in the height of the HoverCard.
  const connectedAIBlockHeight = React.useRef(0);
  const connectedAIBlockRef = useRef(null);
  const onConnectedAIBlockRender = useCallback(() => {
    var _connectedAIBlockRef$, _connectedAIBlockRef$2;
    connectedAIBlockHeight.current = (_connectedAIBlockRef$ = (_connectedAIBlockRef$2 = connectedAIBlockRef.current) === null || _connectedAIBlockRef$2 === void 0 ? void 0 : _connectedAIBlockRef$2.getBoundingClientRect().height) !== null && _connectedAIBlockRef$ !== void 0 ? _connectedAIBlockRef$ : 0;
  }, []);
  const imagePreview = ImagePreview({
    data: data,
    fallbackElementHeight: snippetHeight.current
  });
  return /*#__PURE__*/React.createElement(FlexibleCard, flexibleCardProps, imagePreview, /*#__PURE__*/React.createElement(TitleBlock, _extends({}, titleBlockProps, {
    metadataPosition: SmartLinkPosition.Top
  })), /*#__PURE__*/React.createElement(MetadataBlock, {
    primary: primary,
    secondary: secondary,
    overrideCss: metadataBlockCss,
    maxLines: 1,
    size: SmartLinkSize.Medium
  }), isAISummaryEnabled &&
  /*#__PURE__*/
  // All custom elements should be contained within a CustomBlock.
  // Flex Container components check whether the direct child is a flex blocks. It removes anything that isn't
  React.createElement(CustomBlock, {
    direction: SmartLinkDirection.Vertical,
    onRender: onConnectedAIBlockRender,
    blockRef: connectedAIBlockRef
  }, /*#__PURE__*/React.createElement(ConnectedAIBlock, {
    imagePreview: !!imagePreview,
    bottomPrimary: tertiary,
    url: url
  })), !isAISummaryEnabled && !imagePreview && /*#__PURE__*/React.createElement(SnippetBlock, {
    status: SmartLinkStatus.Resolved
  }), /*#__PURE__*/React.createElement(SnippetBlock, {
    testId: 'hidden-snippet',
    onRender: onSnippetRender,
    blockRef: snippetBlockRef,
    overrideCss: hiddenSnippetStyles
  }), !isAISummaryEnabled && /*#__PURE__*/React.createElement(MetadataBlock, {
    primary: tertiary,
    size: SmartLinkSize.Large,
    overrideCss: metadataBlockCss,
    maxLines: 1,
    status: SmartLinkStatus.Resolved
  }), getBooleanFF('platform.linking-platform.smart-card.enable-hover-card-related-urls') && /*#__PURE__*/React.createElement(RelatedUrlsBlock, {
    url: url,
    size: SmartLinkSize.Small
  }), isAISummaryEnabled && /*#__PURE__*/React.createElement(AISummaryBlock, {
    metadata: [{
      name: ElementName.Provider
    }],
    actions: footerActions,
    aiSummaryMinHeight: connectedAIBlockHeight.current
  }), !isAISummaryEnabled && /*#__PURE__*/React.createElement(FooterBlock, {
    actions: footerActions,
    size: SmartLinkSize.Large,
    overrideCss: footerBlockCss
  }));
};
export default HoverCardResolvedView;